/**
 * Keeps the mobile menu drawer's <details open> attribute in sync with what
 * the drawer actually looks like.
 *
 * The drawer is only visible when BOTH `open` and .menu-opening are set
 * (component-menu-drawer.css: `.js details[open].menu-opening > .menu-drawer`).
 * Dawn sets them at different moments, so an interrupted tap can leave the two
 * disagreeing, and every mismatch reads to the visitor as a dead hamburger:
 *
 *   open, no .menu-opening  -> panel off-screen, but the next tap is handled
 *                              as "close", so nothing appears. Dawn drops the
 *                              attribute at the end of its 400ms close
 *                              animation; a second tap inside that window --
 *                              exactly what someone does when the menu seems
 *                              not to react -- strands it here.
 *   .menu-opening, no open  -> the page is scroll-locked with no menu on it,
 *                              which is the state that feels frozen. A close
 *                              animation left over from an earlier tap can
 *                              remove the attribute after a later tap already
 *                              re-opened the drawer.
 *
 * .menu-opening is the theme's own intent, so it wins: it's restored to `open`
 * immediately, because that pairing is never valid. The other direction waits
 * out Dawn's close animation first, so an ordinary close is left completely
 * alone -- this only ever cleans up after an interrupted one, it never races
 * the theme's handler. Body scroll locks are cleared along with it.
 */
(function () {
  var CLOSE_ANIMATION_GRACE = 550; // Dawn's close animation is 400ms

  function init() {
    document.querySelectorAll('header-drawer, menu-drawer').forEach(function (drawer) {
      var details = drawer.querySelector('details.menu-drawer-container');
      if (!details) return;
      var summary = details.querySelector('summary');
      var timer = null;

      function unlockBody() {
        document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet');
      }

      function sync() {
        var isOpen = details.hasAttribute('open');
        var isOpening = details.classList.contains('menu-opening');
        if (isOpen === isOpening) return;

        if (isOpening) {
          // Drawer is meant to be on screen -- put the attribute back.
          details.setAttribute('open', '');
          if (summary) summary.setAttribute('aria-expanded', 'true');
          return;
        }

        // Visually closed but still flagged open: clear it so the next tap opens.
        details.removeAttribute('open');
        if (summary) summary.setAttribute('aria-expanded', 'false');
        unlockBody();
      }

      new MutationObserver(function () {
        // The "should be visible" repair is safe to run at once; the closing
        // one has to let Dawn's own animation finish first.
        if (details.classList.contains('menu-opening') && !details.hasAttribute('open')) {
          sync();
          return;
        }
        if (timer) clearTimeout(timer);
        timer = setTimeout(sync, CLOSE_ANIMATION_GRACE);
      }).observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
